// src/utils/profiles/profileGate.ts

import type {
  VisitorProfile,
  VisitorProfileGate,
} from "../../types/profiles/visitorProfileTypes";

export type SafeProfileGateReason =
  | "private_profile"
  | "temporarily_unavailable"
  | "account_paused"
  | "account_suspended"
  | "boundary"
  | "unknown";

type GateOptions = {
  ignoreAccountState?: boolean;
};

export const normalizeProfileGateReason = (
  raw?: string | null
): SafeProfileGateReason => {
  const value = String(raw || "")
    .trim()
    .toLowerCase();

  switch (value) {
    case "hidden_by_confidants":
    case "profile_temporarily_limited":
    case "profile_temporarily_unavailable":
    case "temporarily_unavailable":
      return "temporarily_unavailable";

    case "private_profile":
      return "private_profile";

    case "account_paused":
      return "account_paused";

    case "account_suspended":
      return "account_suspended";

    case "boundary":
      return "boundary";

    default:
      return "unknown";
  }
};

export const getProfileGate = (
  profile: VisitorProfile | any
): VisitorProfileGate | null => {
  return profile?.profile_gate ?? null;
};

export const getSafeProfileGateReason = (
  profile: VisitorProfile | any,
  options: GateOptions = {}
): SafeProfileGateReason => {
  const gate = getProfileGate(profile);

  if (gate?.reason) {
    return normalizeProfileGateReason(gate.reason);
  }

  const user = profile?.user ?? {};

  if (!options.ignoreAccountState) {
    if (user?.is_suspended) {
      return "account_suspended";
    }

    if (user?.is_account_paused) {
      return "account_paused";
    }
  }

  // Legacy fallback only. Never expose this text in UI.
  if (profile?.privacy_level === "hidden_by_confidants") {
    return "temporarily_unavailable";
  }

  if (profile?.privacy_level === "privacy_on") {
    return "private_profile";
  }

  return "unknown";
};

export const isProfileGateLimited = (
  profile: VisitorProfile | any,
  options: GateOptions = {}
): boolean => {
  const gate = getProfileGate(profile);

  if (gate?.reason) {
    return true;
  }

  const user = profile?.user ?? {};

  if (!options.ignoreAccountState) {
    if (user?.is_suspended || user?.is_account_paused) {
      return true;
    }
  }

  if (profile?.privacy_level === "hidden_by_confidants") {
    return true;
  }

  if (profile?.privacy_level === "privacy_on") {
    return true;
  }

  return false;
};

export const isOwnerProfileTemporarilyUnavailable = (
  profile: any
): boolean => {
  const reason = getSafeProfileGateReason(profile, {
    ignoreAccountState: true,
  });

  if (reason === "temporarily_unavailable") {
    return true;
  }

  // Backend/model safety fallback.
  if (profile?.is_hidden_by_confidants === true) {
    return true;
  }

  return false;
};

export const getProfileGateTitle = (
  reason: SafeProfileGateReason
): string => {
  switch (reason) {
    case "private_profile":
      return "Private profile";

    case "temporarily_unavailable":
    case "boundary":
      return "Profile unavailable";

    case "account_paused":
      return "Paused account";

    case "account_suspended":
      return "Profile unavailable";

    case "unknown":
    default:
      return "Profile unavailable";
  }
};

export const getProfileGateMessage = (
  reason: SafeProfileGateReason,
  username?: string | null
): string => {
  const handle = username ? `@${username}` : "This profile";

  switch (reason) {
    case "private_profile":
      return `${handle} is private. Only limited profile information is available.`;

    case "temporarily_unavailable":
      return `For personal reasons, ${handle} is not available right now.`;

    case "account_paused":
      return `This account is currently paused for personal reasons.`;

    case "account_suspended":
      return `This profile is currently unavailable.`;

    case "boundary":
      return `This profile is not available right now.`;

    case "unknown":
    default:
      return `This profile is not available right now.`;
  }
};

export const getOwnerProfileGateTitle = (
  reason: SafeProfileGateReason
): string => {
  switch (reason) {
    case "temporarily_unavailable":
      return "Your profile is unavailable";

    case "account_suspended":
      return "Your account is unavailable";

    case "private_profile":
    case "account_paused":
    case "boundary":
    case "unknown":
    default:
      return "Your profile is unavailable";
  }
};

export const getOwnerProfileGateMessage = (
  profile: any,
  reason: SafeProfileGateReason
): string => {
  const backendMessage = profile?.profile_gate?.owner_message;

  if (typeof backendMessage === "string" && backendMessage.trim()) {
    return backendMessage.trim();
  }

  switch (reason) {
    case "temporarily_unavailable":
      return "Your profile is currently unavailable. Please contact TownLIT Support for more information.";

    case "account_suspended":
      return "Your account is currently unavailable. Please contact TownLIT Support for more information.";

    case "private_profile":
    case "account_paused":
    case "boundary":
    case "unknown":
    default:
      return "Your profile is not available right now. Please contact TownLIT Support for more information.";
  }
};